import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LEVELS, fmtPct, fmtDate, FLAG_TEXT } from "@/lib/prime";
import { toast } from "sonner";

function History({ agency }) {
  const [revs, setRevs] = useState(null);
  useEffect(() => {
    api.get(`/prime/agencies/${agency.business_id}/revisions`).then((r) => setRevs(r.data.items)).catch(() => setRevs([]));
  }, [agency.business_id]);
  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Monthly results</p>
        {agency.history.length === 0 ? <p className="text-sm text-slate-500">No months recorded yet.</p> : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="prime-history-list">
            {agency.history.map((h) => (
              <li key={h.period} className="px-3 py-2 text-sm flex flex-wrap gap-x-3 justify-between">
                <span className="font-medium text-slate-800">{h.period_label}</span>
                <span>{h.level || "Level not recorded"} · {fmtPct(h.score_pct) || "Score not recorded"}</span>
                <span className="text-xs text-slate-500 w-full">{h.source} · {h.entry_method === "import" ? "Imported" : "Entered manually"} by {h.updated_by_name} {fmtDate(h.updated_at)}
                  {h.flags.map((f) => <span key={f} className="block text-amber-700">{FLAG_TEXT[f] || f}</span>)}</span>
              </li>))}
          </ul>)}
      </div>
      <div>
        <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Revision history</p>
        {revs === null ? <p className="text-xs text-slate-400">Loading…</p> : revs.length === 0 ? <p className="text-sm text-slate-500">No revisions.</p> : (
          <ul className="max-h-48 overflow-auto text-xs space-y-1" data-testid="prime-revisions">
            {revs.map((r) => (
              <li key={r.id} className="text-slate-600"><b className="capitalize">{r.action}</b> {r.period} · {r.by_name} · {fmtDate(r.at)}
                {" — "}{r.before ? `${r.before.level || "—"} ${fmtPct(r.before.score_pct) || ""}` : "new"} → {r.after ? `${r.after.level || "—"} ${fmtPct(r.after.score_pct) || ""}` : "removed"}</li>))}
          </ul>)}
      </div>
    </div>
  );
}

export function PrimeResultDialog({ agency, onClose, onSaved }) {
  const cur = agency.current;
  const [f, setF] = useState({ period: new Date().toISOString().slice(0, 7), score_pct: "", level: "", source: "Farmers Dashboard scorecard", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try { await api.post(`/prime/agencies/${agency.business_id}/results`, f); toast.success("Prime result saved"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-auto" data-testid="prime-result-dialog">
        <DialogHeader><DialogTitle>Prime result — {agency.business_name}</DialogTitle>
          <DialogDescription>Record the official result from Farmers Prime reporting. Saving a month that already has a result keeps the earlier version in its history.</DialogDescription></DialogHeader>
        {cur && <p className="text-xs text-slate-500">Current: {cur.level || "—"} · {fmtPct(cur.score_pct) || "—"} · {cur.period_label}</p>}
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label htmlFor="pr-period">Reporting month</Label><Input id="pr-period" type="month" value={f.period} onChange={set("period")} data-testid="prime-form-period" /></div>
          <div><Label htmlFor="pr-score">Prime Score (%)</Label><Input id="pr-score" inputMode="decimal" placeholder="e.g. 72.5" value={f.score_pct} onChange={set("score_pct")} data-testid="prime-form-score" /></div>
          <div><Label htmlFor="pr-level">Achievement Level</Label>
            <select id="pr-level" className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={f.level} onChange={set("level")} data-testid="prime-form-level">
              <option value="">Derive from score</option>{LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
          <div><Label htmlFor="pr-source">Source</Label><Input id="pr-source" value={f.source} onChange={set("source")} data-testid="prime-form-source" /></div>
          <div className="sm:col-span-2"><Label htmlFor="pr-note">Note (optional)</Label><Input id="pr-note" value={f.note} onChange={set("note")} data-testid="prime-form-note" /></div>
        </div>
        <p className="text-[11px] text-slate-500">A level is never used to invent a score. If you enter only a level, the score stays “Not recorded”.</p>
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-strong" data-testid="prime-form-save">Save result</Button></div>
        <History agency={agency} />
      </DialogContent>
    </Dialog>
  );
}
